import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  ArrowRight,
  Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const MAX_ATTEMPTS = 5;

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const attemptsRef = useRef(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) { setStatus('error'); return; }
    if (!token) return;

    attemptsRef.current = 0;
    setAttempts(0);

    const poll = async () => {
      if (attemptsRef.current >= MAX_ATTEMPTS) { setStatus('timeout'); return; }
      try {
        const response = await axios.get(
          `${API}/subscription/subscription/status/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPaymentInfo(response.data);
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          toast.success('Suscripcion activada correctamente!');
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else {
          attemptsRef.current += 1;
          setAttempts(attemptsRef.current);
          setTimeout(poll, 2000);
        }
      } catch (error) {
        console.error('Error checking status:', error);
        attemptsRef.current += 1;
        setAttempts(attemptsRef.current);
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [searchParams, token]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-6">
              <Loader2 size={40} className="text-blue-400 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Verificando Pago...</h1>
            <p className="text-slate-400">Por favor espera mientras confirmamos tu pago</p>
            <p className="text-slate-500 text-sm mt-4">Intento {attempts + 1} de {MAX_ATTEMPTS}</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6 neon-glow-green">
              <CheckCircle size={40} className="text-green-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">¡Suscripción Activada!</h1>
            <p className="text-slate-400 mb-6">
              Tu pago ha sido procesado exitosamente. Ya puedes disfrutar de todas las funciones de tu plan.
            </p>
            
            {paymentInfo && (
              <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Total pagado:</span>
                  <span className="text-white font-mono font-bold">
                    ${(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                  </span>
                </div>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/dashboard')}
                className="btn-primary"
                data-testid="go-to-dashboard"
              >
                Ir al Dashboard
                <ArrowRight size={18} className="ml-2" />
              </Button>
              <Button 
                onClick={() => navigate('/recommendations')}
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                Ver Recomendaciones
              </Button>
            </div>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
              <AlertCircle size={40} className="text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Sesión Expirada</h1>
            <p className="text-slate-400 mb-6">
              La sesión de pago ha expirado. Por favor intenta nuevamente.
            </p>
            <Button 
              onClick={() => navigate('/pricing')}
              className="btn-primary"
            >
              Volver a Planes
            </Button>
          </div>
        );

      case 'timeout':
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
              <AlertCircle size={40} className="text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Verificación Tardando</h1>
            <p className="text-slate-400 mb-6">
              No pudimos confirmar tu pago inmediatamente. Si realizaste el pago, 
              tu suscripción se activará en unos minutos. Revisa tu email para confirmación.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/dashboard')}
                className="btn-primary"
              >
                Ir al Dashboard
              </Button>
              <Button 
                onClick={() => window.location.reload()}
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                Reintentar
              </Button>
            </div>
          </div>
        );

      case 'error':
      default:
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <AlertCircle size={40} className="text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Error</h1>
            <p className="text-slate-400 mb-6">
              Hubo un problema al procesar tu solicitud. Por favor contacta soporte si el problema persiste.
            </p>
            <Button 
              onClick={() => navigate('/pricing')}
              className="btn-primary"
            >
              Volver a Planes
            </Button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <Card className="glass-card max-w-lg w-full">
        <CardContent className="py-12">
          {renderContent()}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
